/**
 * @file useThemePreview.ts
 * @description Живой предпросмотр ползунков «Оформления» на канве без записи
 * в черновик (phase19.16, партия SB120, этап 2; uiux-sb120-stage2 §8 —
 * `useFramePreview` + `commora:site-theme-preview`): во время перетаскивания
 * канва получает документ с правкой раздела, витрина меняет переменные
 * через `setProperty` без перезагрузки. Запись — по отпусканию (`update`).
 *
 * Канвы нет (панель вне конструктора) или на канве чужой источник —
 * просмотр дизайна или прошлой версии (`override`) — функция ничего не
 * делает: черновые правки не должны рисоваться поверх чужого вида.
 */

import type { SiteTheme, SiteThemeSection } from "@chaiberi/shared";
import { useCallback, useContext } from "react";

import { BuilderContext } from "../../components/site-builder/builder-context";
import { postToFrame } from "../../components/site-view/preview/useScaledFrame";
import { useCanvasFontRegistry } from "../../components/site-view/shop-fonts-context";
import { themeMessage } from "../../lib/site-view/canvas-messages";

/** Показать на канве правку раздела темы (не запись). */
export type ThemePreview = <S extends SiteThemeSection>(section: S, patch: Partial<SiteTheme[S]>) => void;

export function useThemePreview(theme: SiteTheme): ThemePreview {
  const builder = useContext(BuilderContext);
  const iframeRef = builder?.iframeRef;
  const override = builder?.override ?? null;
  const registry = useCanvasFontRegistry();
  return useCallback(
    <S extends SiteThemeSection>(section: S, patch: Partial<SiteTheme[S]>) => {
      const frame = iframeRef?.current ?? null;
      if (!frame || override) return;
      const next: SiteTheme = { ...theme, [section]: { ...theme[section], ...patch } };
      postToFrame(frame, themeMessage(next, registry));
    },
    [iframeRef, override, registry, theme],
  );
}
